import * as THREE from 'three'
import { Mob } from './Mob'
import Terrain from '../terrain'
import { InventoryItemType, ItemType } from '../items'

const px = 0.0625

const createMat = (color: number, emissive = 0x000000, emissiveIntensity = 0) =>
  new THREE.MeshStandardMaterial({
    color,
    roughness: 0.92,
    metalness: 0,
    emissive,
    emissiveIntensity
  })

const furMat = createMat(0xd49142)
const darkFurMat = createMat(0x6a3d20)
const muzzleMat = createMat(0xf0d09b)
const eyeMat = createMat(0x18120f)
const noseMat = createMat(0x1d1512)
const collarMat = createMat(0x21b7a8)
const cupMat = createMat(0xd7e8e4)
const coffeeMat = createMat(0x4a2616)
const fireMat = createMat(0xff6a00, 0xff3d00, 0.7)
const fireCoreMat = createMat(0xfff06a, 0xffcc2a, 0.9)

export class FineDog extends Mob {
  legFL: THREE.Mesh
  legFR: THREE.Mesh
  legBL: THREE.Mesh
  legBR: THREE.Mesh
  head: THREE.Group
  tail: THREE.Mesh
  flames: THREE.Group[] = []
  cup: THREE.Group

  animationPhase = 0

  constructor(scene: THREE.Scene, terrain: Terrain, position: THREE.Vector3) {
    super(scene, terrain, position)
    this.speed = 1.35
    this.maxHealth = 8
    this.health = this.maxHealth

    const body = new THREE.Mesh(
      new THREE.BoxGeometry(px * 8, px * 8, px * 14),
      furMat
    )
    body.position.set(0, px * 10, 0)
    this.mesh.add(body)

    const chest = new THREE.Mesh(
      new THREE.BoxGeometry(px * 8.5, px * 5, px * 3),
      muzzleMat
    )
    chest.position.set(0, px * 10, px * 5.6)
    this.mesh.add(chest)

    this.head = new THREE.Group()
    this.head.position.set(0, px * 15, px * 7.5)
    this.mesh.add(this.head)
    this.headPart = this.head

    const headBox = new THREE.Mesh(
      new THREE.BoxGeometry(px * 8, px * 8, px * 8),
      furMat
    )
    this.head.add(headBox)

    const muzzle = new THREE.Mesh(
      new THREE.BoxGeometry(px * 5, px * 3, px * 3),
      muzzleMat
    )
    muzzle.position.set(0, -px * 1.2, px * 4.2)
    this.head.add(muzzle)

    const nose = new THREE.Mesh(
      new THREE.BoxGeometry(px * 2, px * 1.1, px * 0.7),
      noseMat
    )
    nose.position.set(0, -px * 0.9, px * 5.9)
    this.head.add(nose)

    const mouth = new THREE.Mesh(
      new THREE.BoxGeometry(px * 3, px * 0.45, px * 0.35),
      eyeMat
    )
    mouth.position.set(0, -px * 2.65, px * 5.9)
    this.head.add(mouth)

    const eyeGeo = new THREE.BoxGeometry(px * 0.8, px * 1.1, px * 0.35)
    const leftEye = new THREE.Mesh(eyeGeo, eyeMat)
    leftEye.position.set(-px * 2, px * 1.1, px * 4.15)
    const rightEye = new THREE.Mesh(eyeGeo, eyeMat)
    rightEye.position.set(px * 2, px * 1.1, px * 4.15)
    this.head.add(leftEye, rightEye)

    const earGeo = new THREE.BoxGeometry(px * 2.2, px * 5.5, px * 1.8)
    const leftEar = new THREE.Mesh(earGeo, darkFurMat)
    leftEar.position.set(-px * 4.4, px * 1.1, -px * 0.7)
    leftEar.rotation.z = -0.18
    const rightEar = new THREE.Mesh(earGeo, darkFurMat)
    rightEar.position.set(px * 4.4, px * 1.1, -px * 0.7)
    rightEar.rotation.z = 0.18
    this.head.add(leftEar, rightEar)

    const collar = new THREE.Mesh(
      new THREE.BoxGeometry(px * 8.6, px * 1.3, px * 3),
      collarMat
    )
    collar.position.set(0, px * 12.8, px * 5.4)
    this.mesh.add(collar)

    const legGeo = new THREE.BoxGeometry(px * 2.5, px * 7, px * 2.5)
    legGeo.translate(0, -px * 3.5, 0)
    this.legFL = new THREE.Mesh(legGeo, furMat)
    this.legFR = new THREE.Mesh(legGeo, furMat)
    this.legBL = new THREE.Mesh(legGeo, furMat)
    this.legBR = new THREE.Mesh(legGeo, furMat)
    this.legFL.position.set(px * 2.7, px * 7, px * 4.7)
    this.legFR.position.set(-px * 2.7, px * 7, px * 4.7)
    this.legBL.position.set(px * 2.7, px * 7, -px * 4.7)
    this.legBR.position.set(-px * 2.7, px * 7, -px * 4.7)
    this.mesh.add(this.legFL, this.legFR, this.legBL, this.legBR)

    const tailGeo = new THREE.BoxGeometry(px * 2, px * 2, px * 7)
    tailGeo.translate(0, 0, -px * 3.5)
    this.tail = new THREE.Mesh(tailGeo, furMat)
    this.tail.position.set(0, px * 13, -px * 7)
    this.tail.rotation.x = -0.65
    this.mesh.add(this.tail)

    this.cup = this.createCoffeeCup()
    this.cup.position.set(px * 5.7, px * 8.7, px * 5.7)
    this.cup.rotation.y = -0.35
    this.mesh.add(this.cup)

    this.addTinyFire(-0.55, 0.05)
    this.addTinyFire(0.58, -0.46)

    this.mesh.traverse(object => {
      if (object instanceof THREE.Mesh) {
        object.castShadow = true
        object.receiveShadow = true
      }
    })
  }

  createCoffeeCup() {
    const cup = new THREE.Group()
    const body = new THREE.Mesh(
      new THREE.BoxGeometry(px * 3, px * 3.2, px * 3),
      cupMat
    )
    const coffee = new THREE.Mesh(
      new THREE.BoxGeometry(px * 2.3, px * 0.25, px * 2.3),
      coffeeMat
    )
    coffee.position.y = px * 1.75
    const handle = new THREE.Mesh(
      new THREE.BoxGeometry(px * 0.75, px * 2.1, px * 0.75),
      cupMat
    )
    handle.position.set(px * 2, 0, 0)
    cup.add(body, coffee, handle)
    return cup
  }

  addTinyFire(x: number, z: number) {
    const fire = new THREE.Group()
    fire.position.set(x, px * 3, z)

    const base = new THREE.Mesh(
      new THREE.BoxGeometry(px * 2.2, px * 3.8, px * 2.2),
      fireMat
    )
    base.rotation.y = Math.PI / 4

    const core = new THREE.Mesh(
      new THREE.BoxGeometry(px * 1.2, px * 3, px * 1.2),
      fireCoreMat
    )
    core.position.y = px * 0.3
    core.rotation.y = -Math.PI / 4

    fire.add(base, core)
    this.flames.push(fire)
    this.mesh.add(fire)
  }

  updateAnimation(delta: number): void {
    this.animationPhase += delta * (this.state === 'walking' ? 9 : 2.5)

    if (this.state === 'walking') {
      const swing = Math.sin(this.animationPhase) * 0.5
      this.legFL.rotation.x = swing
      this.legBL.rotation.x = -swing
      this.legFR.rotation.x = -swing
      this.legBR.rotation.x = swing
      this.head.rotation.x = Math.sin(this.animationPhase * 0.7) * 0.07
      this.tail.rotation.y = Math.sin(this.animationPhase * 1.4) * 0.35
    } else {
      this.legFL.rotation.x = THREE.MathUtils.lerp(this.legFL.rotation.x, 0, delta * 5)
      this.legBL.rotation.x = THREE.MathUtils.lerp(this.legBL.rotation.x, 0, delta * 5)
      this.legFR.rotation.x = THREE.MathUtils.lerp(this.legFR.rotation.x, 0, delta * 5)
      this.legBR.rotation.x = THREE.MathUtils.lerp(this.legBR.rotation.x, 0, delta * 5)
      this.head.rotation.x = Math.sin(this.animationPhase) * 0.035
      this.tail.rotation.y = Math.sin(this.animationPhase * 1.2) * 0.16
    }

    this.cup.rotation.z = Math.sin(this.animationPhase * 0.6) * 0.035
    this.flames.forEach((fire, index) => {
      const flicker = 1 + Math.sin(this.animationPhase * 3 + index) * 0.12
      fire.scale.set(flicker, 0.9 + flicker * 0.12, flicker)
      fire.rotation.y += delta * (1.5 + index * 0.35)
    })
  }

  getDrops(): InventoryItemType[] {
    return [ItemType.bone, ItemType.stick]
  }
}
